import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  AdminApi,
  AdminCourse,
  AdminTestResultGradingStatus,
  AdminTestResultListItem,
} from '../../services/adminApi'
import { formatDbDateTimeVi } from '../../utils/dbDateDisplay'

const gradingBadgeClass = (status: AdminTestResultGradingStatus) => {
  if (status === 'pending') return 'bg-amber-100 text-amber-800'
  if (status === 'graded') return 'bg-green-100 text-green-800'
  return 'bg-slate-100 text-slate-700'
}

const gradingBadgeLabel = (status: AdminTestResultGradingStatus) => {
  if (status === 'pending') return 'Chưa chấm'
  if (status === 'graded') return 'Đã chấm'
  return 'Tự chấm'
}

const AdminTestResultsPage = () => {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<AdminTestResultListItem[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [courses, setCourses] = useState<AdminCourse[]>([])
  const [courseId, setCourseId] = useState<number | undefined>()
  const [gradingStatus, setGradingStatus] = useState<AdminTestResultGradingStatus | ''>('')
  const [q, setQ] = useState('')

  const loadData = async (overrides?: {
    page?: number
    courseId?: number | undefined
    gradingStatus?: AdminTestResultGradingStatus | ''
    q?: string
  }) => {
    const nextPage = overrides?.page ?? page
    const nextCourseId = overrides && 'courseId' in overrides ? overrides.courseId : courseId
    const nextGradingStatus =
      overrides && 'gradingStatus' in overrides ? overrides.gradingStatus : gradingStatus
    const nextQ = overrides && 'q' in overrides ? overrides.q : q

    setLoading(true)
    try {
      const res = await AdminApi.listTestResults({
        courseId: nextCourseId,
        gradingStatus: nextGradingStatus || undefined,
        q: nextQ?.trim() || undefined,
        page: nextPage,
        limit: 20,
      })
      setItems(res.data.items)
      setTotalPages(res.data.pagination.totalPages)
      setTotal(res.data.pagination.total)
    } catch {
      toast.error('Không tải được danh sách kết quả test')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    ;(async () => {
      try {
        const res = await AdminApi.getCourses()
        setCourses(res.data.items)
      } catch {
        toast.error('Không tải được danh sách khóa học')
      }
    })()
  }, [])

  useEffect(() => {
    void loadData({ page })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const applyFilters = () => {
    if (page === 1) {
      void loadData({ page: 1 })
    } else {
      setPage(1)
    }
  }

  const resetFilters = () => {
    setCourseId(undefined)
    setGradingStatus('')
    setQ('')
    if (page !== 1) setPage(1)
    void loadData({ page: 1, courseId: undefined, gradingStatus: '', q: '' })
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-m2-on-surface">Quản lý kết quả test</h1>
          <p className="mt-1 text-sm text-m2-on-surface-variant">
            Xem danh sách bài làm của học viên và chấm điểm câu tự luận.
          </p>
        </div>
        <div className="text-sm text-m2-on-surface-variant">Tổng: {total}</div>
      </div>

      <div className="rounded-lg border border-m2-outline-variant bg-m2-surface-container p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <select
            className="rounded border border-m2-outline px-3 py-2 text-base"
            value={courseId ?? ''}
            onChange={(e) => setCourseId(e.target.value ? Number(e.target.value) : undefined)}
          >
            <option value="">Tất cả khóa học</option>
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.code} - {course.name} {course.is_active || course.isActive ? '(ACTIVE)' : ''}
              </option>
            ))}
          </select>
          <select
            className="rounded border border-m2-outline px-3 py-2 text-base"
            value={gradingStatus}
            onChange={(e) =>
              setGradingStatus((e.target.value || '') as AdminTestResultGradingStatus | '')
            }
          >
            <option value="">Tất cả trạng thái</option>
            <option value="pending">Chưa chấm</option>
            <option value="graded">Đã chấm</option>
            <option value="auto">Tự chấm</option>
          </select>
          <input
            type="search"
            className="rounded border border-m2-outline px-3 py-2 text-base"
            placeholder="Tìm tên, email, SĐT..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyFilters()
            }}
          />
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded bg-m2-primary px-3 py-2 text-base text-white hover:bg-eh-forest-shadow"
              onClick={applyFilters}
            >
              Tìm
            </button>
            <button
              type="button"
              className="rounded border border-m2-outline px-3 py-2 text-base text-m2-on-surface hover:bg-m2-surface-low"
              onClick={resetFilters}
            >
              Xóa lọc
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-m2-outline-variant bg-m2-surface-container">
        <div className="overflow-x-auto">
          <table className="min-w-full text-base">
            <thead className="bg-m2-surface-low text-left text-sm uppercase text-m2-on-surface-variant">
              <tr>
                <th className="px-3 py-2">Thời gian</th>
                <th className="px-3 py-2">Học viên</th>
                <th className="px-3 py-2">Bài test</th>
                <th className="px-3 py-2">Khóa học</th>
                <th className="px-3 py-2">Điểm</th>
                <th className="px-3 py-2">Trạng thái</th>
                <th className="px-3 py-2">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td className="px-3 py-4 text-center text-m2-on-surface-variant" colSpan={7}>
                    Đang tải...
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td className="px-3 py-4 text-center text-m2-on-surface-variant" colSpan={7}>
                    Không có dữ liệu
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((row) => (
                  <tr key={row.id} className="border-t border-m2-outline-variant align-top">
                    <td className="px-3 py-2 text-sm text-m2-on-surface-variant whitespace-nowrap">
                      <div>#{row.id}</div>
                      <div>{formatDbDateTimeVi(row.savedAt || row.createdAt)}</div>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="font-medium text-m2-on-surface">{row.fullName || '-'}</div>
                      <div className="text-m2-on-surface-variant">{row.email || '-'}</div>
                      <div className="text-m2-on-surface-variant">{row.phone || '-'}</div>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="font-medium text-m2-on-surface">{row.paperName || '-'}</div>
                    </td>
                    <td className="px-3 py-2 text-sm text-m2-on-surface-variant">
                      {row.courseName || '-'}
                    </td>
                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                      <div className="font-medium text-m2-on-surface">
                        {row.point}/{row.maxPoint}
                      </div>
                      <div className="text-m2-on-surface-variant">{row.percent}%</div>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <span
                        className={`inline-flex rounded px-2 py-1 text-sm font-medium ${gradingBadgeClass(row.gradingStatus)}`}
                      >
                        {gradingBadgeLabel(row.gradingStatus)}
                      </span>
                      {row.pendingEssayCount > 0 ? (
                        <div className="mt-1 text-xs text-amber-700">
                          {row.pendingEssayCount} câu chờ chấm
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/admin/test-results/${row.id}`}
                        className="inline-flex rounded border border-m2-primary px-2 py-1 text-sm text-m2-primary hover:bg-m2-primary/10"
                      >
                        Chi tiết
                      </Link>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          className="rounded border border-m2-outline px-3 py-1 text-sm"
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
        >
          Trước
        </button>
        <span className="text-sm text-m2-on-surface-variant">
          Trang {page} / {totalPages || 1}
        </span>
        <button
          type="button"
          className="rounded border border-m2-outline px-3 py-1 text-sm"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          Sau
        </button>
      </div>
    </div>
  )
}

export default AdminTestResultsPage
